import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);
  const [impersonator, setImpersonator] = useState(null);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      
      // Check impersonation status
      try {
        const impersonationResponse = await axios.get(`${API_URL}/auth/impersonation-status`);
        if (impersonationResponse.data.impersonating) {
          setImpersonating(true);
          setImpersonator(impersonationResponse.data.impersonator);
        } else {
          setImpersonating(false);
          setImpersonator(null);
        }
      } catch (e) {
        setImpersonating(false);
        setImpersonator(null);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setImpersonating(false);
    setImpersonator(null);
    return userData;
  };

  const logout = async () => {
    try {
      // Call backend logout endpoint
      if (token) {
        await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('adminToken'); // Clear any stored admin token
      delete axios.defaults.headers.common['Authorization'];
      setToken(null);
      setUser(null);
      setImpersonating(false);
      setImpersonator(null);
    }
  };

  const impersonateUser = async (userId) => {
    try {
      // Store current admin token before impersonating
      localStorage.setItem('adminToken', token);
      
      const response = await axios.post(`${API_URL}/admin/impersonate/${userId}`);
      const { access_token, user: userData, impersonator: impersonatorData } = response.data;
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(userData);
      setImpersonating(true);
      setImpersonator(impersonatorData);
      
      return userData;
    } catch (error) {
      console.error('Failed to impersonate user:', error);
      throw error;
    }
  };

  const impersonateCompany = async (companyId) => {
    try {
      localStorage.setItem('adminToken', token);

      const response = await axios.post(`${API_URL}/admin/companies/${companyId}/impersonate`);
      const { access_token, user: userData, impersonator: impersonatorData } = response.data;

      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(userData);
      setImpersonating(true);
      setImpersonator(impersonatorData);

      return userData;
    } catch (error) {
      console.error('Failed to impersonate company:', error);
      throw error;
    }
  };

  const stopImpersonating = async () => {
    try {
      const response = await axios.post(`${API_URL}/admin/stop-impersonate`);
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.removeItem('adminToken');
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(userData);
      setImpersonating(false);
      setImpersonator(null);
      
      return userData;
    } catch (error) {
      console.error('Failed to stop impersonating:', error);
      // Fallback: try to use stored admin token
      const adminToken = localStorage.getItem('adminToken');
      if (adminToken) {
        localStorage.setItem('token', adminToken);
        localStorage.removeItem('adminToken');
        axios.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
        setToken(adminToken);
        await fetchUser();
      }
      throw error;
    }
  };

  const forgotPassword = async (email) => {
    await axios.post(`${API_URL}/auth/forgot-password`, null, { params: { email } });
  };

  const hasRole = (roles) => {
    if (!user) return false;
    if (typeof roles === 'string') return user.role === roles;
    return roles.includes(user.role);
  };

  const isCompanyRep = () => hasRole('COMPANY_REP');
  const isCompanyOwner = () => isCompanyRep() && !!user?.is_company_owner;
  const isAdmin = () => hasRole('ADMIN');
  const isCRD = () => hasRole('CRD');
  const isChairman = () => hasRole('CHAIRMAN');
  const isCoordinator = () => hasRole('COORDINATOR');
  const isPI = () => hasRole(['PI', 'INVESTIGATOR']); // Legacy + unified role
  const isReviewer = () => hasRole('REVIEWER');
  const isConsultant = () => hasRole(['CONSULTANT', 'INVESTIGATOR']); // Legacy + unified role
  const isInvestigator = () => hasRole(['INVESTIGATOR', 'PI', 'CONSULTANT']); // Unified check
  const isDirector = () => hasRole('DIRECTOR');
  const isExecDirector = () => hasRole('EXEC_DIRECTOR');
  const isFinance = () => hasRole('FINANCE');
  const isInternal = () => hasRole(['ADMIN', 'CRD', 'CHAIRMAN', 'COORDINATOR', 'PI', 'REVIEWER', 'CONSULTANT', 'INVESTIGATOR', 'DIRECTOR', 'EXEC_DIRECTOR', 'FINANCE']);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      logout,
      forgotPassword,
      hasRole,
      isCompanyRep,
      isCompanyOwner,
      isAdmin,
      isCRD,
      isChairman,
      isCoordinator,
      isPI,
      isReviewer,
      isConsultant,
      isInvestigator,
      isDirector,
      isExecDirector,
      isFinance,
      isInternal,
      // Impersonation
      impersonating,
      impersonator,
      impersonateUser,
      impersonateCompany,
      stopImpersonating
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
